import React, { useCallback, useEffect, useMemo, useState } from 'react';
import appStoreBadge from '../assets/stores/app-store.svg';
import googlePlayBadge from '../assets/stores/google-play.svg';
import { registerReferralFingerprint } from '../api/referralTracking.api';

const REDIRECT_DELAY_MS = 3000;
const PACKAGE_NAME = 'com.beautybook.customer';
const APPLE_APP_ID = '6450213994';
const PLAY_STORE_HTTP = `https://play.google.com/store/apps/details?id=${PACKAGE_NAME}`;
const APP_STORE_HTTP = `https://apps.apple.com/app/id${APPLE_APP_ID}`;

const ANDROID_SCHEME = 'beautybookapp';
const IOS_SCHEME = 'com.beautybook.customer';
const REFERRAL_ROUTE = 'referral';
const DOWNLOAD_PATH = '/download-app';
const FINGERPRINT_STORAGE_KEY = 'beautybook-referral-fingerprint';

const palette = {
  accent: '#F7C72E',
  primary: '#8501A5',
  dark: '#1b2c1f',
};

const mobileShellStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 1100,
  backgroundColor: 'transparent',
  borderRadius: 28,
};

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${palette.dark} 0%, ${palette.primary} 60%, ${palette.accent} 120%)`,
    padding: '2rem',
  },
  shell: {
    width: '100%',
    maxWidth: 1150,
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    padding: '3rem',
    boxShadow: '0 50px 110px rgba(27, 44, 31, 0.45)',
    border: `1px solid rgba(247, 199, 46, 0.35)`,
    borderRadius: 34,
  },
  hero: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
    borderRadius: 24,
    background: `linear-gradient(135deg, rgba(247, 199, 46, 0.2), rgba(133, 1, 165, 0.18))`,
    padding: '2rem',
    marginBottom: '2rem',
    border: `1px solid rgba(27, 44, 31, 0.1)`,
  },
  heroTitle: {
    fontSize: '2rem',
    fontWeight: 700,
    color: palette.dark,
    margin: 0,
  },
  heroSubtitle: {
    fontSize: '1.05rem',
    color: palette.primary,
    margin: 0,
  },
  ctaRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.75rem',
    marginTop: '1rem',
  },
  primaryCta: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.85rem 1.75rem',
    borderRadius: 999,
    border: 'none',
    background: `linear-gradient(120deg, ${palette.primary}, ${palette.accent})`,
    color: '#ffffff',
    fontWeight: 600,
    fontSize: '1rem',
    cursor: 'pointer',
    boxShadow: `0 25px 45px rgba(133, 1, 165, 0.35)`,
    textDecoration: 'none',
  },
  secondaryCta: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.85rem 1.25rem',
    borderRadius: 999,
    border: `1px solid rgba(247, 199, 46, 0.6)`,
    color: palette.dark,
    fontWeight: 600,
    backgroundColor: '#fff',
    cursor: 'pointer',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '1.5rem',
  },
  infoCard: {
    borderRadius: 20,
    border: `1px solid rgba(247, 199, 46, 0.35)`,
    padding: '1.6rem',
    boxShadow: '0 25px 45px rgba(27, 44, 31, 0.08)',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
  },
  infoTitle: {
    fontSize: '1.15rem',
    fontWeight: 600,
    color: palette.primary,
    marginBottom: '0.75rem',
  },
  infoText: {
    fontSize: '0.95rem',
    color: 'rgba(27, 44, 31, 0.85)',
    lineHeight: 1.6,
    marginBottom: '1rem',
  },
  storeRow: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
  },
  referralBadge: {
    marginTop: '0.75rem',
    padding: '0.65rem 1rem',
    borderRadius: 12,
    backgroundColor: 'rgba(247, 199, 46, 0.2)',
    color: palette.dark,
    fontWeight: 600,
    fontSize: '0.95rem',
  },
  statusRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.5rem',
    marginTop: '0.75rem',
  },
  statusBadge: {
    padding: '0.5rem 0.9rem',
    borderRadius: 10,
    backgroundColor: 'rgba(133, 1, 165, 0.1)',
    color: palette.dark,
    fontWeight: 500,
    fontSize: '0.85rem',
  },
  qrWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap' as const,
  },
  shareInput: {
    flex: 1,
    minWidth: 180,
    borderRadius: 12,
    border: `1px solid rgba(247, 199, 46, 0.6)`,
    padding: '0.75rem 1rem',
    fontSize: '0.9rem',
    color: palette.dark,
    backgroundColor: 'rgba(247, 199, 46, 0.12)',
  },
};

const copyTextToClipboard = async (value: string) => {
  if (!value) {
    return;
  }

  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const tempElement = document.createElement('textarea');
  tempElement.value = value;
  tempElement.style.position = 'fixed';
  tempElement.style.opacity = '0';
  document.body.appendChild(tempElement);
  tempElement.focus();
  tempElement.select();
  document.execCommand('copy');
  document.body.removeChild(tempElement);
};


async function getPublicIp() {
  const r = await fetch('https://api.ipify.org?format=json');
  const data = await r.json();
  return data.ip; // e.g. "203.0.113.10"
}

const collectFingerprintSeed = async () => {
  if (typeof window === 'undefined') {
    return 'browser';
  }
  const ua = navigator.userAgent;
  const isAndroid = /Android/i.test(ua);
  const isIos = /iPhone|iPad|iPod/i.test(ua);

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const resolution = `${window.screen?.width || 0}x${window.screen?.height || 0}`;
  const pixelRatio = window.devicePixelRatio?.toString() ?? '1';
  const language = navigator.language || '';
  const userAgent = (await getPublicIp()) || '';
  const platform = isAndroid ? 'android' : isIos ? 'ios' : 'web';
 
  return [userAgent, platform, language, tz, resolution, pixelRatio].join('::');
};

const ensureFingerprint = async (raw = false) => {
  if (typeof window === 'undefined') {
    return 'browser';
  }

  try {
    const seed = await collectFingerprintSeed();

    return seed;
  } catch (error) {
    console.error('Failed to generate fingerprint', error);
    return 'fallback-fingerprint';
  }
};

const buildAppLink = (
  platform: 'android' | 'ios',
  referralCode?: string | null,
  giftId?: string | null,
) => {
  const queryParams = new URLSearchParams();
  if (referralCode) {
    queryParams.set('ref', referralCode);
  }
  if (giftId) {
    queryParams.set('gift', giftId);
  }
  const query = queryParams.toString();
  const querySuffix = query ? `?${query}` : '';

  if (platform === 'android') {
    return `${ANDROID_SCHEME}://${REFERRAL_ROUTE}${querySuffix}`;
  }

  return `${IOS_SCHEME}://${REFERRAL_ROUTE}${querySuffix}`;
};

const getShareBaseUrl = () => {
  if (typeof window === 'undefined') {
    return DOWNLOAD_PATH;
  }

  try {
    const base = new URL(DOWNLOAD_PATH, window.location.origin);
    return base.toString();
  } catch {
    return `${window.location.origin}${DOWNLOAD_PATH}`;
  }
};

const buildShareLink = (referralCode?: string | null, giftId?: string | null) => {
  const baseUrl = getShareBaseUrl();
  if (!referralCode && !giftId) {
    return baseUrl;
  }

  const queryParams = new URLSearchParams();
  if (referralCode) {
    queryParams.set('ref', referralCode);
  }
  if (giftId) {
    queryParams.set('gift', giftId);
  }

  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}${queryParams.toString()}`;
};

const NewCustomerDeepLinkHandler = () => {
  const [referralCode, setReferralCode] = useState<string | null | undefined>(undefined);
  const [giftId, setGiftId] = useState<string | null | undefined>(undefined);
  const [rawSeed, setRawSeed] = useState<string>('');
  const [targetUrl, setTargetUrl] = useState<string>('');
  const [countdown, setCountdown] = useState<number>(Math.floor(REDIRECT_DELAY_MS / 1000));
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const [autoCopyStatus, setAutoCopyStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [fingerprintStatus, setFingerprintStatus] = useState<
    'idle' | 'pending' | 'success' | 'error'
  >('idle');

  const deviceInfo = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return { isAndroid: false, isIos: false, isMobile: false };
    }

    const ua = navigator.userAgent;
    const isAndroid = /Android/i.test(ua);
    const isIos = /iPhone|iPad|iPod/i.test(ua);

    return { isAndroid, isIos, isMobile: isAndroid || isIos };
  }, []);

  const shareLink = useMemo(
    () =>
      buildShareLink(
        typeof referralCode === 'undefined' ? undefined : referralCode,
        typeof giftId === 'undefined' ? undefined : giftId,
      ),
    [referralCode, giftId],
  );

  const qrSource = useMemo(
    () =>
      shareLink
        ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
            shareLink,
          )}`
        : '',
    [shareLink],
  );

  const shellStyle = deviceInfo.isMobile ? mobileShellStyle : (styles.shell as React.CSSProperties);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('ref') || params.get('referralCode') || params.get('code');
    const gift = params.get('gift');
    setReferralCode(code);
    setGiftId(gift);
  }, []);

  useEffect(() => {
    if (!referralCode) {
      return;
    }

    let canceled = false;

    const syncFingerprint = async () => {
      try {
        setFingerprintStatus('pending');
        const rawFingerprint = await ensureFingerprint(true);

        setRawSeed(rawFingerprint);
        if (canceled) {
          return;
        }

        await registerReferralFingerprint({
          fingerprint: rawFingerprint,
          referralCode: referralCode || null,
          metadata: {
            shareLink,
            isMobile: deviceInfo.isMobile,
            userAgent: navigator?.userAgent,
            language: navigator?.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            platform: navigator?.platform,
            screen:
              typeof window !== 'undefined'
                ? `${window.screen?.width || 0}x${window.screen?.height || 0}`
                : undefined,
          },
        });

        if (!canceled) {
          setFingerprintStatus('success');
        }
      } catch (error) {
        console.error('Failed to register referral fingerprint', error);
        if (!canceled) {
          setFingerprintStatus('error');
        }
      }
    };

    syncFingerprint();

    return () => {
      canceled = true;
    };
  }, [referralCode, shareLink, deviceInfo.isMobile]);

  useEffect(() => {
    if (!deviceInfo.isMobile) {
      setCountdown(0);
      return;
    }

    if (typeof referralCode === 'undefined' || typeof giftId === 'undefined') {
      return;
    }

    const fallbackLink = deviceInfo.isAndroid ? PLAY_STORE_HTTP : APP_STORE_HTTP;
    setTargetUrl(fallbackLink);

    const schemeLink = buildAppLink(
      deviceInfo.isAndroid ? 'android' : 'ios',
      referralCode || undefined,
      giftId || undefined,
    );
    window.location.href = schemeLink;

    const redirectTimer = window.setTimeout(() => {
      window.location.href = fallbackLink;
    }, REDIRECT_DELAY_MS);

    return () => {
      window.clearTimeout(redirectTimer);
    };
  }, [deviceInfo.isAndroid, deviceInfo.isMobile, referralCode, giftId]);

  useEffect(() => {
    if (!deviceInfo.isMobile || !targetUrl) {
      return;
    }

    setCountdown(Math.floor(REDIRECT_DELAY_MS / 1000));
    const interval = window.setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [deviceInfo.isMobile, targetUrl]);

  const handleCopyLink = useCallback(async () => {
    if (!shareLink) {
      return;
    }

    try {
      await copyTextToClipboard(shareLink);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 2000);
    } catch (error) {
      console.error('Failed to copy share link', error);
      setCopyState('error');
      window.setTimeout(() => setCopyState('idle'), 2000);
    }
  }, [shareLink]);

  const referralChip =
    referralCode && typeof referralCode === 'string' ? `Referral Code: ${referralCode}` : null;

  const heroSubtitle = referralChip
    ? 'We will pass your referral code to the Daif app once it opens.'
    : 'We are preparing the Daif app store page for you.';

  const storeCtaLink = deviceInfo.isAndroid
    ? PLAY_STORE_HTTP
    : deviceInfo.isIos
    ? APP_STORE_HTTP
    : PLAY_STORE_HTTP;

  return (
    <div style={styles.page as React.CSSProperties}>
      <div style={shellStyle}>
        <section
          style={{
            ...styles.hero,
            background: deviceInfo.isMobile
              ? '#f9f3fa'
              : `linear-gradient(135deg, rgba(247, 199, 46, 0.15), rgba(133, 1, 165, 0.18))`,
          }}
        >
          <div>
            <p style={styles.heroTitle as React.CSSProperties}>Daif - You&apos;re Almost In!</p>
            <p style={styles.heroSubtitle as React.CSSProperties}>{heroSubtitle}</p>
          </div>
          <div style={styles.ctaRow as React.CSSProperties}>
            <a href={storeCtaLink} style={styles.primaryCta as React.CSSProperties}>
              Go to app store
            </a>
            <button
              type="button"
              style={styles.secondaryCta as React.CSSProperties}
              onClick={handleCopyLink}
            >
              {copyState === 'copied'
                ? 'Link copied!'
                : copyState === 'error'
                ? 'Copy failed'
                : 'Copy share link'}
            </button>
          </div>
          {deviceInfo.isMobile && targetUrl && countdown > 0 && (
            <p style={{ marginTop: '0.5rem', color: palette.primary, fontWeight: 500 }}>
              Redirecting automatically in {countdown}s...
            </p>
          )}
          {rawSeed}
          {referralChip && (
            <div style={styles.statusRow as React.CSSProperties}>
              <div style={styles.referralBadge as React.CSSProperties}>{referralChip}</div>
              {autoCopyStatus === 'success' && (
                <div style={styles.statusBadge as React.CSSProperties}>
                  Referral code copied to your clipboard for quick paste in Daif.
                </div>
              )}
              {autoCopyStatus === 'error' && (
                <div
                  style={{
                    ...(styles.statusBadge as React.CSSProperties),
                    backgroundColor: 'rgba(247, 199, 46, 0.25)',
                  }}
                >
                  Couldn&apos;t copy automatically - tap &quot;Copy share link&quot; to do it
                  yourself.
                </div>
              )}
              {fingerprintStatus === 'pending' && (
                <div style={styles.statusBadge as React.CSSProperties}>Saving your referral...</div>
              )}
              {fingerprintStatus === 'success' && (
                <div style={styles.statusBadge as React.CSSProperties}>
                  Referral saved - install Daif anytime and we&apos;ll restore it.
                </div>
              )}
              {fingerprintStatus === 'error' && (
                <div
                  style={{
                    ...(styles.statusBadge as React.CSSProperties),
                    backgroundColor: 'rgba(247, 199, 46, 0.25)',
                  }}
                >
                  Couldn&apos;t sync with the server - keep this link handy just in case.
                </div>
              )}
            </div>
          )}
        </section>

        <section style={styles.infoGrid as React.CSSProperties}>
          <div style={styles.infoCard as React.CSSProperties}>
            <p style={styles.infoTitle as React.CSSProperties}>Download on your phone</p>
            <p style={styles.infoText as React.CSSProperties}>
              Tap the store badge that matches your device. We will carry your referral code through
              installation so you receive any associated rewards automatically.
            </p>

            <div style={styles.storeRow as React.CSSProperties}>
              <a
                href={APP_STORE_HTTP}
                target="_blank"
                rel="noreferrer"
                style={{ textAlign: 'left' }}
              >
                <img src={appStoreBadge} alt="Download on the App Store" style={{ width: 180 }} />
              </a>
              <a
                href={PLAY_STORE_HTTP}
                target="_blank"
                rel="noreferrer"
                style={{ textAlign: 'left' }}
              >
                <img src={googlePlayBadge} alt="Get it on Google Play" style={{ width: 180 }} />
              </a>
            </div>
          </div>

          <div style={styles.infoCard as React.CSSProperties}>
            <p style={styles.infoTitle as React.CSSProperties}>Working from a computer?</p>
            <p style={styles.infoText as React.CSSProperties}>
              Scan the QR code with your phone or copy the share link and send it to yourself.
              Opening that link on mobile will trigger the same deep link experience.
            </p>

            <div style={styles.qrWrap as React.CSSProperties}>
              {qrSource ? (
                <img
                  src={qrSource}
                  alt="Scan to download Daif"
                  style={{
                    width: 160,
                    height: 160,
                    border: `1px solid rgba(247, 199, 46, 0.35)`,
                  }}
                />
              ) : null}
              <div style={{ flex: 1, minWidth: 200 }}>
                <div
                  style={{
                    fontSize: '0.8rem',
                    textTransform: 'uppercase',
                    color: palette.primary,
                    marginBottom: '0.25rem',
                  }}
                >
                  shareable link
                </div>
                <input
                  readOnly
                  value={shareLink}
                  style={styles.shareInput as React.CSSProperties}
                />
              </div>
            </div>
          </div>

          <div style={styles.infoCard as React.CSSProperties}>
            <p style={styles.infoTitle as React.CSSProperties}>What happens next?</p>
            <ul
              style={{
                paddingLeft: '1.2rem',
                color: 'rgba(27, 44, 31, 0.85)',
                lineHeight: 1.65,
                marginBottom: 0,
              }}
            >
              <li>
                We try to open the Daif app directly. If it is not installed, you will land on the
                store.
              </li>
              <li>After installation, open Daif - your referral code travels with you.</li>
              <li>Sign up to unlock your welcome perks and start exploring cafes immediately.</li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
};

export default NewCustomerDeepLinkHandler;
